import type { DriveStep } from 'driver.js';
import { defineStore } from 'pinia';
import { useTourStore } from './tourStore';
import { useSidebarStore } from '../sidebarStore';

export const useOfficeShopTourStore = defineStore('officeShopTour', () => {
  const tourStore = useTourStore();
  const sidebarStore = useSidebarStore();
  // when the user does not have a stripe account properly setup, the tour cannot move forward because those components are behind a setup wall / basically hidden until an account is connected
  const officeShopTourSteps: DriveStep[] = [
    {
      element: '#office-shop-tour-anchor',
      popover: {
        title: 'Praxisshop',
        description:
          'Hier verwaltest du deine Selbstzahlerangebote und die Zuordnung zu deinen Therapeuten.',
        side: 'top',
        onNextClick: () => {
          tourStore.setTourToComplete();
          tourStore.driverObject?.moveNext();
        },
      },
    },
    {
      // SearchToolbar renders the add button twice: a compact one for narrow
      // `@container/gt` widths and the `-desktop` variant that is the visible one
      // from @mobileLandscape up. Tours only start from the ≥1024px help page, so
      // target the desktop variant — the compact one is `display:none` there and
      // driver.js would highlight an invisible element.
      element: '#add-therapist-button-desktop',
      popover: {
        title: 'Leistung hinzufügen',
        description:
          '<span class="font-bold">Klicke hier,</span> um ein digitales Selbstzahlerangebot für deinen Praxisshop zu erstellen.',
        side: 'top',
        nextBtnText: `<span class="opacity-50">Weiter →</span>`,
        onPopoverRender: async () => {
          await tourStore.useListenToClick(
            '#add-therapist-button-desktop',
            tourStore.driverObject,
            2,
          );
        },
        onNextClick: () => {},
      },
    },
    {
      element: '#office-product-name',
      popover: {
        title: 'Leistung',
        description: 'Dieser Name wird deinem Patienten zu dieser Leistung im Shop angezeigt.',
        side: 'top',
        onPrevClick: async () => {
          // close sidebar
          sidebarStore.closeSidebar({ type: 'officeProductDetails', action: 'cancel' });
          tourStore.driverObject?.movePrevious();
        },
      },
    },
    {
      element: '#office-product-price',
      popover: {
        title: 'Preisübersicht',
        description:
          'Diesen Preis bezahlt dein Patient über seinen Therame-Account. Du siehst hier auch deinen Ertrag je verkaufter Selbstzahlerleistung.',
        side: 'top',
      },
    },
    {
      element: '#office-product-description',
      popover: {
        title: 'Beschreibung',
        description: 'Hier kannst du deine Leistung bewerben.',
        side: 'top',
      },
    },
    {
      element: '#office-product-type-select',
      popover: {
        title: 'Typ',
        description:
          'Entscheide, ob deine Leistung digital oder in der Praxis stattfinden soll. Bei digitalen Angeboten über Therame kannst du festlegen, wie viele Übungsprogramme und/oder Videosprechstunden du in diesem Paket anbieten möchtest.',
        side: 'top',
      },
    },
    {
      element: '#office-product-performance-period',
      popover: {
        title: 'Leistungszeitraum',
        description:
          'In diesem Zeitraum muss die Leistung von dir erbracht werden. Du kannst diese selbst festlegen.',
        side: 'top',
      },
    },
  ] as const;

  return {
    officeShopTourSteps,
  };
});
